import { GameSize } from "@/types/game";

/**
 * Desired size rhythm for one 12-column row of catalog cells.
 * Spreads 3×3 and 2×2 tiles among 1×1 tiles instead of clustering heroes at the top.
 */
export const CATALOG_GRID_STAGGER_PATTERN: readonly GameSize[] = [
  GameSize.Large,
  GameSize.Small,
  GameSize.Medium,
  GameSize.Small,
  GameSize.Small,
  GameSize.Medium,
  GameSize.Small,
  GameSize.Large,
  GameSize.Small,
  GameSize.Medium,
  GameSize.Small,
  GameSize.Small,
];

type SizedCatalogGame = {
  id?: string;
  slug: string;
  size?: number | null;
};

function catalogGameKey(game: SizedCatalogGame): string {
  return game.id ?? game.slug;
}

function gameSizeOf(game: SizedCatalogGame): GameSize {
  return (game.size ?? GameSize.Small) as GameSize;
}

function buildSizeQueues<T extends SizedCatalogGame>(games: T[]): Record<GameSize, T[]> {
  const queues: Record<GameSize, T[]> = {
    [GameSize.Small]: [],
    [GameSize.Medium]: [],
    [GameSize.Large]: [],
  };

  for (const game of games) {
    queues[gameSizeOf(game)].push(game);
  }

  return queues;
}

function takeFromQueue<T extends SizedCatalogGame>(
  queues: Record<GameSize, T[]>,
  preferred: GameSize
): T | undefined {
  if (queues[preferred].length > 0) {
    return queues[preferred].shift();
  }

  const fallbacks: GameSize[] =
    preferred === GameSize.Large
      ? [GameSize.Medium, GameSize.Small]
      : preferred === GameSize.Medium
        ? [GameSize.Small, GameSize.Large]
        : [GameSize.Medium, GameSize.Large];

  for (const size of fallbacks) {
    if (queues[size].length > 0) {
      return queues[size].shift();
    }
  }

  for (const size of [GameSize.Small, GameSize.Medium, GameSize.Large] as const) {
    if (queues[size].length > 0) {
      return queues[size].shift();
    }
  }

  return undefined;
}

/**
 * Reorder popularity-sorted games so tile sizes stagger through the grid.
 * Preserves within-tier popularity (queues dequeue in original order).
 * Any leftovers keep their relative popularity order at the end.
 */
export function staggerGamesForCatalogGrid<T extends SizedCatalogGame>(
  popularitySortedGames: T[]
): T[] {
  if (popularitySortedGames.length <= 1) {
    return popularitySortedGames;
  }

  const queues = buildSizeQueues(popularitySortedGames);
  const placed = new Set<string>();
  const result: T[] = [];

  let patternIndex = 0;
  let misses = 0;

  while (
    result.length < popularitySortedGames.length &&
    misses < CATALOG_GRID_STAGGER_PATTERN.length
  ) {
    const preferred =
      CATALOG_GRID_STAGGER_PATTERN[patternIndex % CATALOG_GRID_STAGGER_PATTERN.length];
    patternIndex += 1;

    const picked = takeFromQueue(queues, preferred);
    if (!picked) {
      misses += 1;
      continue;
    }

    misses = 0;
    placed.add(catalogGameKey(picked));
    result.push(picked);
  }

  for (const game of popularitySortedGames) {
    const key = catalogGameKey(game);
    if (placed.has(key)) continue;
    result.push(game);
    placed.add(key);
  }

  return result;
}
